import React, {
  useEffect,
  useRef,
  useState,
  Suspense,
  lazy,
  useMemo,
} from "react";
import {
  Grid,
  Tooltip,
  Typography,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from "@mui/material";
import axios from "axios";
import { config, termsAndConditionsAPI } from "../../../config";
import NotFound from "../../../components/NotFount";
import { Create } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import Layout from "../../../layout/Layout";
import Breadcrumbs from "@mui/material/Breadcrumbs";
import Link from "@mui/material/Link";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import { LoadingButton } from "@mui/lab";
import AddIcon from "@mui/icons-material/Add";
import DataTable from "react-data-table-component";
import EditLocationOutlinedIcon from "@mui/icons-material/EditLocationOutlined";
import IconButton from "@mui/material/IconButton";
import { Tooltip as ReactTooltip } from "react-tooltip";
import dayjs from "dayjs";
import * as XLSX from "xlsx";
import { fitColumns } from "../../../utils/excelFit";
import { writeXlsxFrozen } from "../../../utils/xlsxFreeze";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { apiData } from "../../../services/api_service";
import { useParams } from "react-router-dom";
import {
  dataTableCustomStyles,
  serialNumberColumn,
} from "../../../components/dataTableStyles";

function TermsConditionsList() {
  const navigate = useNavigate();
  const { orderType } = useParams();
  const [lstCompany, setCompanyList] = useState([]),
    [lstVendorType, setVendorTypeList] = useState([]);

  let tempRefs = useRef(false);

  useEffect(() => {
    if (tempRefs.current === false) {
      return () => {
        tempRefs.current = true;
      };
    }
  }, []);

  const [totalCount, setTotalCount] = useState(0);
  const [termsandConditions, setTermsandConditions] = useState([]);

  const [columnName, setColumnName] = useState("");
  const [sortDirection, setSortDirection] = useState("");

  const handleEditTermsAndCondition = (id) => {
    navigate(`/terms/add/${orderType}/${id}`);
  };

  // Export all terms & conditions for this order type (every row, not just the
  // current page) with the same columns shown in the table.
  const handleExportData = async () => {
    try {
      setLoading(true);
      const response = await apiData("get", termsAndConditionsAPI, {
        pageIndex: 0,
        pageSize: 1000000,
        termsType: orderType === "purchase" ? 1 : 2,
      });
      const dataArray = Array.isArray(response.data?.result)
        ? response.data.result
        : [];
      if (!dataArray.length) {
        alert("No data to export!");
        return;
      }
      const exportData = dataArray.map((row, i) => ({
        "#": i + 1,
        Title: row.title || "",
        Status: row.status === 1 ? "Active" : "Inactive",
      }));
      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      fitColumns(ws);
      XLSX.utils.book_append_sheet(wb, ws, "Terms & Conditions");
      const label = orderType === "purchase" ? "purchase" : "invoice";
      writeXlsxFrozen(
        XLSX,
        wb,
        `terms_conditions_${label}_${dayjs().format("DD_MM_YYYY")}.xlsx`
      );
    } catch (error) {
      console.error("Export failed:", error);
      alert("Failed to export data.");
    } finally {
      setLoading(false);
    }
  };

  const [loading, setLoading] = useState(false);
  const [rowsPerPage, setRowsPerPage] = useState(100); //change to 10 after you remove paginationRowsPerPageOptions
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    if (tempRefs.current === false) {
      fetchUsers(1, rowsPerPage, 3);
    }
  }, [orderType]);

  const fetchUsers = async () => {
    const payload = {
      pageIndex: currentPage,
      pageSize: rowsPerPage,
      termsType: orderType === "purchase" ? 1 : 2,
    };
    try {
      const response = await apiData("get", termsAndConditionsAPI, payload);

      setTermsandConditions(response.data.result);
      setTotalCount(response.data.totalCount);
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  };

  const handlePageChange = (page) => {
    tempRefs.current = false;
    fetchUsers(page, rowsPerPage, 16, columnName, sortDirection);
    setCurrentPage(page);
  };

  const handleRowsPerPageChange = async (newRowsPerPage) => {
    if (!termsandConditions.length) return; //when the table is rendered for the first time, this would trigger, and we don't need to call fetchAttendanceData again

    fetchUsers(1, newRowsPerPage, 16, columnName, sortDirection);
    // downloadExcel(1, rowsPerPage);
    setRowsPerPage(newRowsPerPage);
    setCurrentPage(1);
  };
  const handleSort = (column, sortDirection) => {
    if (column.name) {
      let columnName = column.name.toLowerCase();
      setColumnName(columnName);
      setSortDirection(sortDirection);

      fetchUsers(1, rowsPerPage, 16, columnName, sortDirection); // Ensure fetchAttendanceData is being called
    }
  };

  const columns = useMemo(() => [
    serialNumberColumn(currentPage, rowsPerPage),
    {
      name: "Title",
      field: "id",
      sortable: true,
      selector: (row) => row.title,
    },
    {
      name: "Status",
      field: "id",
      sortable: true,
      cell: (row) => (
        <Typography
          className={`${
            row.status === 1 ? `active-status` : `inactive-status`
          } `}
        >
          {row.status === 1 ? "Active" : "Inactive"}
        </Typography>
      ),
    },
    {
      name: "Action",
      field: "id",
      cell: (row) => (
        <IconButton
          color="success"
          onClick={() => handleEditTermsAndCondition(row.id)}
          aria-label="add an alarm"
        >
          <EditLocationOutlinedIcon />
        </IconButton>
      ),
    },
  ]);

  return (
    <Layout>
      <Grid className="employee-form bold-header">
        <Grid container spacing={3}>
          <Grid
            textAlign="center"
            item
            className="form-lable"
            xs={12}
            lg={6}
            md={6}
          >
            <Typography className="page_title" textAlign="left">
              {orderType === "purchase" ? "Purchase Orders" : "Invoices"} -
              Terms & Conditions
            </Typography>

            <Breadcrumbs aria-label="breadcrumb">
              <Link underline="hover" color="inherit" href="/dashboard">
                Dashboard
              </Link>
              <Typography color="text.primary">Terms & Conditions</Typography>
              <Typography color="text.primary">List</Typography>
            </Breadcrumbs>
          </Grid>
          <Grid
            textAlign="right"
            item
            className="form-lable"
            xs={12}
            lg={6}
            md={6}
          >
            <LoadingButton
              size="medium"
              variant="contained"
              color="success"
              className="button_first"
              onClick={handleExportData}
              startIcon={<FileDownloadIcon />}
            >
              Export
            </LoadingButton>
            <LoadingButton
              size="medium"
              variant="contained"
              color="success"
              className="button_first"
              href={`/terms/add/${orderType}`} // <-- use curly braces
              startIcon={<AddIcon />}
            >
              Add
            </LoadingButton>
            <LoadingButton
              size="medium"
              variant="contained"
              color="primary"
              onClick={() => navigate(-1)}
              startIcon={<ArrowBackIosIcon />}
            >
              Back
            </LoadingButton>
          </Grid>
        </Grid>
        <Grid className="form_design">
          <Grid className="small_table">
            <DataTable
              pagination
              paginationServer
              fixedHeader
              fixedHeaderScrollHeight="550px"
              striped
              customStyles={dataTableCustomStyles}
              columns={columns}
              data={termsandConditions}
              progressPending={loading}
              paginationTotalRows={totalCount}
              paginationDefaultPage={currentPage}
              paginationRowsPerPageOptions={[1, 10, 25, 50, 100]} //you can remove it later, just to have more pages
              paginationPerPage={rowsPerPage}
              onChangePage={handlePageChange}
              onSort={handleSort}
              onChangeRowsPerPage={handleRowsPerPageChange}
            />
          </Grid>
        </Grid>
      </Grid>
    </Layout>
  );
}

export default TermsConditionsList;
